import React from "react";
import { CommandPalette } from "../CommandPalette";
import type { CommandPaletteHint } from "../CommandPalette";
import { VoicePillTeaser } from "./VoicePillTeaser";
import { ShortcutsDialog } from "../ShortcutsDialog";
import { COMMAND_PALETTE_COPY } from "./CommandPaletteHost.copy";
import type { CommandPaletteHostProps } from "./CommandPaletteHost.types";
import { useCommandPaletteHostState } from "./useCommandPaletteHostState";
import { useCommandFreeText } from "../../../contexts/CommandFreeTextContext";
import { useCharacterKeys } from "../../../services/commands/characterKeys";

const HINTS: readonly CommandPaletteHint[] = [
  { chords: [{ key: "ArrowUp" }, { key: "ArrowDown" }], label: COMMAND_PALETTE_COPY.moveShort },
  { chords: [{ key: "Enter" }], label: COMMAND_PALETTE_COPY.runShort },
  // Voice is on Shift+Mod+V (bound by the host); Mod+M minimises the window on a Mac.
  { chords: [{ key: "v", mod: true, shift: true }], label: COMMAND_PALETTE_COPY.voice },
];
const END_HINTS: readonly CommandPaletteHint[] = [{ chords: [{ key: "?" }], label: COMMAND_PALETTE_COPY.allShortcuts }];

/** Mounts the command palette and the shortcuts sheet once, driven by the command registry. */
export const CommandPaletteHost: React.FC<CommandPaletteHostProps> = ({ searchEnd, insetLeft }) => {
  const state = useCommandPaletteHostState();
  const registered = useCommandFreeText().insetLeft;
  const [characterKeysOn, setCharacterKeysOn] = useCharacterKeys();
  return (
    <>
      <CommandPalette
        insetLeft={insetLeft ?? registered}
        open={state.paletteOpen}
        title={COMMAND_PALETTE_COPY.title}
        placeholder={COMMAND_PALETTE_COPY.placeholder}
        emptyLabel={COMMAND_PALETTE_COPY.empty}
        query={state.query}
        onQueryChange={state.setQuery}
        groups={state.groups}
        crumb={state.crumb}
        onBack={state.onBack}
        onSelect={state.onSelect}
        onClose={state.close}
        searchEnd={searchEnd?.(state.setQuery) ?? <VoicePillTeaser label={COMMAND_PALETTE_COPY.voiceListening} />}
        hints={HINTS}
        endHints={END_HINTS}
      />
      <ShortcutsDialog
        open={state.shortcutsOpen}
        title={COMMAND_PALETTE_COPY.shortcutsTitle}
        closeLabel={COMMAND_PALETTE_COPY.close}
        groups={state.shortcutGroups}
        columns={2}
        onClose={state.closeShortcuts}
        setting={{
          label: COMMAND_PALETTE_COPY.characterKeys,
          description: COMMAND_PALETTE_COPY.characterKeysDescription,
          value: characterKeysOn,
          onChange: setCharacterKeysOn,
        }}
      />
    </>
  );
};

export default CommandPaletteHost;
